/**
 * Navigation Simple Direct Guidance - No Mappings, No Complexity
 * Every requirement ID maps directly to its guidance
 * Based on Regulations Assistant verified guidance
 */

export const NAV_SIMPLE_GUIDANCE = {
  // GPS Systems
  'nav.gps': {
    id: 'nav.gps',
    name: 'GPS Receiver',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.1',
    description: 'Global Positioning System receiver for navigation.',
    authority: 'WBC3 Section 19.1',
    placement: {
      requirements: [
        'Required for all commercial vessels',
        'Primary position fixing system',
        'Must be accessible to helmsman'
      ]
    },
    specifications: {
      type: 'GPS receiver with chart plotting capability',
      standards: 'WBC3 Section 19.1 compliance required',
      mounting: 'Navigation position with clear antenna view'
    },
    maintenance: {
      mandatory: [
        'Regular accuracy verification',
        'Antenna connection inspection',
        'Chart datum verification',
        'Battery backup testing'
      ]
    }
  },

  // Compass Systems
  'nav.compass.magnetic': {
    id: 'nav.compass.magnetic',
    name: 'Magnetic Compass',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.2',
    description: 'Primary magnetic steering compass.',
    authority: 'WBC3 Section 19.2',
    placement: {
      requirements: [
        'Primary steering compass required',
        'Must be visible from steering position',
        'Protected from magnetic interference'
      ]
    },
    specifications: {
      type: 'Magnetic steering compass',
      standards: 'WBC3 Section 19.2 compliance required',
      mounting: 'Steering position with clear visibility'
    },
    maintenance: {
      mandatory: [
        'Annual adjustment and deviation card',
        'Regular deviation checks',
        'Lighting system verification',
        'Gimbal operation check'
      ]
    }
  },

  'nav.compass.bearing': {
    id: 'nav.compass.bearing',
    name: 'Bearing Compass',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.2',
    description: 'Hand bearing compass for navigation bearings.',
    authority: 'WBC3 Section 19.2',
    placement: {
      requirements: [
        'Required for position fixing',
        'Backup to electronic navigation',
        'Must be readily accessible'
      ]
    },
    specifications: {
      type: 'Hand bearing compass',
      standards: 'WBC3 Section 19.2 compliance required',
      mounting: 'Readily accessible navigation position'
    },
    maintenance: {
      mandatory: [
        'Annual accuracy verification',
        'Deviation assessment',
        'Physical condition inspection',
        'Lighting functionality check'
      ]
    }
  },

  'nav.compass.light': {
    id: 'nav.compass.light',
    name: 'Compass Lighting',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.2',
    description: 'Compass lighting system for night navigation.',
    authority: 'WBC3 Section 19.2',
    placement: {
      requirements: [
        'Red lighting for night vision preservation',
        'Dimmer control recommended',
        'Emergency backup lighting'
      ]
    },
    specifications: {
      type: 'Red compass lighting with dimmer',
      standards: 'WBC3 Section 19.2 compliance required',
      mounting: 'Integrated with compass installation'
    },
    maintenance: {
      mandatory: [
        'Monthly lighting operation test',
        'Dimmer control verification',
        'Emergency backup functionality',
        'Red filter integrity check'
      ]
    }
  },

  // Electronic Navigation
  'nav.ecdis': {
    id: 'nav.ecdis',
    name: 'Electronic Chart Display System',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.3',
    description: 'Electronic Chart Display and Information System.',
    authority: 'WBC3 Section 19.3',
    placement: {
      requirements: [
        'Enhanced electronic navigation capability',
        'Chart plotting and position display',
        'Integration with other navigation systems'
      ]
    },
    specifications: {
      type: 'ECDIS with chart plotting capability',
      standards: 'WBC3 Section 19.3 compliance required',
      mounting: 'Navigation position with operator access'
    },
    maintenance: {
      mandatory: [
        'Chart data updates',
        'System software updates',
        'Display calibration verification',
        'Backup power system test'
      ]
    }
  },

  'nav.echo.sounder': {
    id: 'nav.echo.sounder',
    name: 'Echo Sounder',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.4',
    description: 'Echo sounder for depth measurement.',
    authority: 'WBC3 Section 19.4',
    placement: {
      requirements: [
        'Depth measurement capability required',
        'Safe navigation in shallow waters',
        'Audible alarm capability'
      ]
    },
    specifications: {
      type: 'Echo sounder with depth alarms',
      standards: 'WBC3 Section 19.4 compliance required',
      mounting: 'Through-hull transducer installation'
    },
    maintenance: {
      mandatory: [
        'Transducer cleaning and inspection',
        'Calibration verification',
        'Alarm function testing',
        'Display accuracy check'
      ]
    }
  },

  'nav.radar.3cm': {
    id: 'nav.radar.3cm',
    name: 'Radar System (3cm)',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.5',
    description: '3cm radar system for navigation and collision avoidance.',
    authority: 'WBC3 Section 19.5',
    placement: {
      requirements: [
        'Required for certain categories and operations',
        'Collision avoidance capability',
        'Clear antenna installation required'
      ]
    },
    specifications: {
      type: '3cm radar with display and controls',
      standards: 'WBC3 Section 19.5 compliance required',
      mounting: 'Clear antenna view and operator position'
    },
    maintenance: {
      mandatory: [
        'Annual performance verification',
        'Antenna rotation and bearing check',
        'Display calibration verification',
        'Magnetron performance assessment'
      ]
    }
  },

  'nav.radar.reflector': {
    id: 'nav.radar.reflector',
    name: 'Radar Reflector',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.6',
    description: 'Radar reflector for enhanced visibility to other vessels.',
    authority: 'WBC3 Section 19.6',
    placement: {
      requirements: [
        'Enhanced radar visibility required',
        'Passive radar enhancement',
        'High mounting position preferred'
      ]
    },
    specifications: {
      type: 'Radar reflector (passive or active)',
      standards: 'WBC3 Section 19.6 compliance required',
      mounting: 'High position with clear radar coverage'
    },
    maintenance: {
      mandatory: [
        'Annual mounting security check',
        'Physical condition inspection',
        'Reflective surface verification',
        'Installation integrity assessment'
      ]
    }
  },

  'nav.ais.transceiver': {
    id: 'nav.ais.transceiver',
    name: 'AIS Transceiver',
    category: 'Navigation',
    regulation: 'WBC3 Section 17.6',
    description: 'Automatic Identification System for vessel tracking.',
    authority: 'WBC3 Section 17.6',
    placement: {
      requirements: [
        'Required for vessels ≥300GT or passenger vessels',
        'Automatic vessel identification',
        'GPS integration required'
      ]
    },
    specifications: {
      type: 'AIS Class A or B transceiver',
      standards: 'WBC3 Section 17.6 compliance required',
      mounting: 'Navigation position with GPS antenna'
    },
    maintenance: {
      mandatory: [
        'Monthly data verification',
        'Static data accuracy check',
        'Transmission verification',
        'GPS integration confirmation'
      ]
    }
  },

  'nav.log.distance': {
    id: 'nav.log.distance',
    name: 'Distance Log',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.7',
    description: 'Distance and speed measurement system.',
    authority: 'WBC3 Section 19.7',
    placement: {
      requirements: [
        'Speed and distance measurement',
        'Navigation planning capability',
        'Integration with chart systems'
      ]
    },
    specifications: {
      type: 'Electronic or mechanical distance log',
      standards: 'WBC3 Section 19.7 compliance required',
      mounting: 'Through-hull sensor or GPS-based'
    },
    maintenance: {
      mandatory: [
        'Calibration verification',
        'Sensor cleaning (if applicable)',
        'Accuracy assessment',
        'Display functionality check'
      ]
    }
  },

  // Charts and Publications
  'nav.charts': {
    id: 'nav.charts',
    name: 'Navigational Charts',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.8',
    description: 'Current navigational charts for operating areas.',
    authority: 'WBC3 Section 19.8',
    placement: {
      requirements: [
        'Current charts for all operating areas',
        'Appropriate scale for navigation',
        'Regular updates required'
      ]
    },
    specifications: {
      type: 'Current navigational charts (paper or electronic)',
      standards: 'WBC3 Section 19.8 compliance required',
      mounting: 'Chart table or electronic display system'
    },
    maintenance: {
      mandatory: [
        'Regular chart updates',
        'Currency verification',
        'Notice to mariners application',
        'Chart condition assessment'
      ]
    }
  },

  'nav.publications.nautical': {
    id: 'nav.publications.nautical',
    name: 'Nautical Publications',
    category: 'Navigation',
    regulation: 'WBC3 Section 19.8',
    description: 'Required nautical publications and references.',
    authority: 'WBC3 Section 19.8',
    placement: {
      requirements: [
        'Tide tables, light lists, sailing directions',
        'Current editions required',
        'Accessible to navigator'
      ]
    },
    specifications: {
      type: 'Current nautical publications',
      standards: 'WBC3 Section 19.8 compliance required',
      mounting: 'Navigation library accessible storage'
    },
    maintenance: {
      mandatory: [
        'Annual publication updates',
        'Currency verification',
        'Supplement application',
        'Physical condition maintenance'
      ]
    }
  },

  // Lights and Signals
  'nav.lights': {
    id: 'nav.lights',
    name: 'Navigation Lights',
    category: 'Navigation',
    regulation: 'WBC3 Section 18.1',
    description: 'Navigation lights for vessel identification.',
    authority: 'WBC3 Section 18.1',
    placement: {
      requirements: [
        'Port, starboard, stern, and masthead lights',
        'Appropriate for vessel size and type',
        'Visibility requirements compliance'
      ]
    },
    specifications: {
      type: 'LED navigation lights (recommended)',
      standards: 'WBC3 Section 18.1 and ColRegs compliance',
      mounting: 'Regulation positions with clear visibility'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Lens cleaning and inspection',
        'Lamp replacement as required',
        'Electrical connection verification'
      ]
    }
  },

  'nav.lamp.signalling': {
    id: 'nav.lamp.signalling',
    name: 'Signalling Lamp',
    category: 'Navigation',
    regulation: 'WBC3 Section 18.2',
    description: 'Signalling lamp for visual communications.',
    authority: 'WBC3 Section 18.2',
    placement: {
      requirements: [
        'Visual communication capability',
        'Morse code signalling',
        'Readily accessible from bridge'
      ]
    },
    specifications: {
      type: 'Aldis lamp or equivalent signalling device',
      standards: 'WBC3 Section 18.2 compliance required',
      mounting: 'Bridge position with 360-degree coverage'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Lens and reflector cleaning',
        'Battery or power supply check',
        'Focusing mechanism verification'
      ]
    }
  },

  'nav.shapes.day': {
    id: 'nav.shapes.day',
    name: 'Day Shapes',
    category: 'Navigation',
    regulation: 'WBC3 Section 18.3',
    description: 'Day shapes for vessel status indication.',
    authority: 'WBC3 Section 18.3',
    placement: {
      requirements: [
        'Black shapes for daytime signalling',
        'Appropriate for vessel operations',
        'Readily deployable when required'
      ]
    },
    specifications: {
      type: 'Black day shapes (balls, cones, diamonds)',
      standards: 'WBC3 Section 18.3 and ColRegs compliance',
      mounting: 'Mast or prominent position for visibility'
    },
    maintenance: {
      mandatory: [
        'Annual condition inspection',
        'Hoisting equipment verification',
        'Shape integrity assessment',
        'Storage condition monitoring'
      ]
    }
  },

  'nav.sound.signal': {
    id: 'nav.sound.signal',
    name: 'Sound Signal Apparatus',
    category: 'Navigation',
    regulation: 'WBC3 Section 18.4',
    description: 'Sound signalling equipment for navigation.',
    authority: 'WBC3 Section 18.4',
    placement: {
      requirements: [
        'Horn or whistle for sound signals',
        'Appropriate range for vessel size',
        'Readily operable from bridge'
      ]
    },
    specifications: {
      type: 'Air horn, electric horn, or whistle',
      standards: 'WBC3 Section 18.4 and ColRegs compliance',
      mounting: 'High position for sound propagation'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Sound level verification',
        'Compressor operation (if air horn)',
        'Electrical connection check'
      ]
    }
  },

  // Police Boat Specific
  'nav.anchor.police.cat2to3': {
    id: 'nav.anchor.police.cat2to3',
    name: 'Police Boat Anchor (Cat 2-3)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Anchor system for police boats operating in Categories 2-3.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'Police operation anchoring capability',
        'Categories 2-3 specific requirements',
        'Rapid deployment capability'
      ]
    },
    specifications: {
      type: 'Police boat anchor with rapid deployment',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Bow or stern rapid deployment system'
    },
    maintenance: {
      mandatory: [
        'Monthly deployment test',
        'Chain and rode inspection',
        'Windlass operation verification',
        'Rapid deployment mechanism check'
      ]
    }
  },

  'nav.anchor.police.cat5to6': {
    id: 'nav.anchor.police.cat5to6',
    name: 'Police Boat Anchor (Cat 5-6)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Anchor system for police boats operating in Categories 5-6.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'Police operation anchoring capability',
        'Categories 5-6 local operations',
        'Manual deployment acceptable'
      ]
    },
    specifications: {
      type: 'Police boat anchor system',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Appropriate for local operations'
    },
    maintenance: {
      mandatory: [
        'Monthly deployment test',
        'Anchor and chain inspection',
        'Manual handling verification',
        'Storage security check'
      ]
    }
  },

  'nav.barometer.police': {
    id: 'nav.barometer.police',
    name: 'Barometer (Police Operations)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Barometer for police boat weather monitoring.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'Weather monitoring for police operations',
        'Atmospheric pressure measurement',
        'Navigation position installation'
      ]
    },
    specifications: {
      type: 'Aneroid or digital barometer',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Protected navigation position'
    },
    maintenance: {
      mandatory: [
        'Annual calibration verification',
        'Monthly reading accuracy check',
        'Physical condition inspection',
        'Environmental protection assessment'
      ]
    }
  },

  'nav.emergency.power.police': {
    id: 'nav.emergency.power.police',
    name: 'Emergency Power (Police Navigation)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Emergency power for police boat navigation systems.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'Emergency power for navigation systems',
        'Police operation continuity',
        'Battery backup or portable generator'
      ]
    },
    specifications: {
      type: 'Emergency power system for navigation',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Protected installation with navigation access'
    },
    maintenance: {
      mandatory: [
        'Monthly emergency power test',
        'Battery condition and charging check',
        'Navigation system operation verification',
        'Transfer switch operation test'
      ]
    }
  },

  'nav.publications.police.alternative': {
    id: 'nav.publications.police.alternative',
    name: 'Police Navigation Publications (Alternative)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Alternative navigation publications for police operations.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'Alternative to standard nautical publications',
        'Police-specific navigation requirements',
        'Local area operational information'
      ]
    },
    specifications: {
      type: 'Police operation navigation references',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Accessible reference storage'
    },
    maintenance: {
      mandatory: [
        'Regular publication updates',
        'Local area information verification',
        'Police operation relevance check',
        'Accessibility maintenance'
      ]
    }
  },

  'nav.searchlight.police': {
    id: 'nav.searchlight.police',
    name: 'Searchlight (Police Operations)',
    category: 'Navigation',
    regulation: 'WBC3 Police Boat Requirements',
    description: 'Searchlight for police boat operations.',
    authority: 'WBC3 Police Boat Requirements',
    placement: {
      requirements: [
        'High-intensity searchlight for police operations',
        'Remote control capability preferred',
        'Wide area illumination capability'
      ]
    },
    specifications: {
      type: 'High-intensity searchlight with remote control',
      standards: 'WBC3 police boat compliance required',
      mounting: 'Elevated position with 360-degree coverage'
    },
    maintenance: {
      mandatory: [
        'Monthly operation and positioning test',
        'Lamp replacement as required',
        'Remote control system verification',
        'Electrical connection inspection'
      ]
    }
  },

  // Missing ID identified by audit - Emergency Navigation Power
  
  'electrical.emergency.nav.power': {
    id: 'electrical.emergency.nav.power',
    name: 'Emergency Navigation Power',
    equipment_type: 'emergency_power',
    
    placement: {
      requirements: [
        'Emergency power supply for essential navigation equipment',
        'Independent from main power system',
        'Accessible for maintenance and testing',
        'Protected from damage and weather'
      ]
    },
    
    specifications: {
      type: 'Emergency power system for navigation equipment',
      standards: 'Emergency power per WBC3 electrical requirements',
      trigger: 'Essential navigation equipment requiring backup power',
      power_options: [
        'Emergency battery system',
        'Portable generator',
        'UPS system for critical navigation equipment'
      ],
      capacity: 'Adequate power for essential navigation systems',
      independence: 'Independent power source from main electrical system',
      duration: 'Sufficient duration for emergency navigation requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual emergency power system testing',
        'Verify navigation equipment operation on emergency power',
        'Check battery condition and charging system',
        'Test automatic transfer capability if fitted'
      ],
      recommended: [
        'Monthly emergency power system test',
        'Monitor battery charge levels and condition',
        'Check navigation equipment compatibility with emergency power',
        'Verify crew familiarity with emergency power procedures'
      ]
    },
    
    authority: 'WBC3 Emergency Power Requirements'
  }
};

/**
 * Simple Direct Lookup - No mappings, no complexity
 * @param {string} itemId - Equipment requirement ID
 * @returns {Object|null} Guidance object or null
 */
export function getNAVSimpleGuidance(itemId) {
  return NAV_SIMPLE_GUIDANCE[itemId] || null;
}